import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../utils/api';
import { formatCurrency } from '../utils/helpers';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Users, FileText, Money, ChartLine, UserPlus, Receipt, Wallet,
  Crown, Gift, Lightning, Check, Star, WhatsappLogo, Clock
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  const benefits = [
    { icon: Users, title: 'Unlimited Enquiry', desc: 'सर्व enquiry track करा' },
    { icon: Receipt, title: 'Professional Invoices', desc: 'WhatsApp वर share करा' },
    { icon: Wallet, title: 'Payment Tracking', desc: 'Pending payments track करा' },
    { icon: ChartLine, title: 'Business Analytics', desc: 'Revenue analytics' },
    { icon: WhatsappLogo, title: 'WhatsApp Integration', desc: 'Direct messaging' },
    { icon: Star, title: 'Remove Branding', desc: 'Profile वरून branding हटवा' },
  ];

  useEffect(() => {
    fetchStats();
    checkPremiumStatus();
  }, []);

  const checkPremiumStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsPremium(response.data.is_active);
      setSubscriptionStatus(response.data);
      localStorage.setItem('is_premium', response.data.is_active ? 'true' : 'false');
    } catch (error) {
      setIsPremium(localStorage.getItem('is_premium') === 'true');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await dashboardAPI.getStats();
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-[#6B7280]">Loading...</div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Enquiry',
      value: stats?.total_leads || 0,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      subtitle: `${stats?.new_leads || 0} new`,
    },
    {
      title: 'Customers',
      value: stats?.total_customers || 0,
      icon: UserPlus,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      subtitle: 'Total registered',
    },
    {
      title: 'Invoices',
      value: stats?.total_invoices || 0,
      icon: FileText,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      subtitle: 'Generated',
    },
    {
      title: 'Total Revenue',
      value: formatCurrency(stats?.total_revenue || 0),
      icon: Money,
      color: 'text-[#FF5A36]',
      bgColor: 'bg-orange-50',
      subtitle: 'All time',
    },
    {
      title: 'Pending Amount',
      value: formatCurrency(stats?.pending_amount || 0),
      icon: Wallet,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      subtitle: 'Outstanding',
    },
    {
      title: 'Conversion Rate',
      value: `${stats?.conversion_rate || 0}%`,
      icon: ChartLine,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      subtitle: 'Enquiry to customer',
    },
  ];

  const quickActions = [
    { label: 'New Enquiry', to: '/leads', icon: UserPlus, testId: 'quick-action-new-lead' },
    { label: 'Create Invoice', to: '/invoices/create', icon: Receipt, testId: 'quick-action-create-invoice' },
    { label: 'View Payments', to: '/payments', icon: Wallet, testId: 'quick-action-view-payments' },
    { label: 'View Customers', to: '/customers', icon: Users, testId: 'quick-action-view-customers' },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24 md:pb-8" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-['Chivo'] text-4xl md:text-5xl font-black tracking-tighter text-[#121212] mb-2">
            Dashboard
          </h1>
          <p className="text-[#6B7280] font-medium leading-relaxed">Overview of your business</p>
        </div>

        {/* Premium Status Banner */}
        {isPremium ? (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <Crown className="h-5 w-5 text-green-600" weight="fill" />
              </div>
              <div>
                <p className="font-bold text-green-800">Premium Active ✓</p>
                <p className="text-sm text-green-600">
                  Valid till: {subscriptionStatus?.end_date ? new Date(subscriptionStatus.end_date).toLocaleDateString('mr-IN') : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gradient-to-r from-orange-50 to-pink-50 border border-orange-200 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <Gift className="h-5 w-5 text-orange-600" weight="duotone" />
                </div>
                <div>
                  <p className="font-bold text-gray-900">Free Plan Active</p>
                  <p className="text-sm text-gray-600">10 Enquiry + 10 Invoices FREE</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/premium')}
                className="bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-full text-sm"
              >
                <Crown className="h-4 w-4 mr-1" weight="fill" /> Upgrade
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-8">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card
                key={index}
                className="stat-card border-[#E5E7EB] bg-white"
                data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                  <div className={`${stat.bgColor} p-2 rounded-lg`}>
                    <Icon className={`h-4 w-4 ${stat.color}`} weight="duotone" />
                  </div>
                </CardHeader>
                <CardContent>
                  <CardTitle className="text-xs font-medium text-[#6B7280] mb-1">
                    {stat.title}
                  </CardTitle>
                  <div className="text-2xl font-black font-['Chivo'] tracking-tighter text-[#121212]">
                    {stat.value}
                  </div>
                  <p className="text-xs text-[#6B7280] mt-1">{stat.subtitle}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card className="border-[#E5E7EB] bg-white mb-8" data-testid="quick-actions-card">
          <CardHeader>
            <CardTitle className="font-['Chivo'] text-xl font-bold tracking-tight">
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {quickActions.map((action, index) => {
                const Icon = action.icon;
                return (
                  <Link key={index} to={action.to}>
                    <button
                      data-testid={action.testId}
                      className="w-full flex flex-col items-center justify-center p-6 border border-[#E5E7EB] rounded-lg hover:border-[#FF5A36] hover:bg-orange-50 transition-all group"
                    >
                      <Icon className="h-8 w-8 text-[#6B7280] group-hover:text-[#FF5A36] mb-2" weight="duotone" />
                      <span className="text-sm font-medium text-[#121212] group-hover:text-[#FF5A36]">
                        {action.label}
                      </span>
                    </button>
                  </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Premium Plans Section for Free Users */}
        {!isPremium && (
          <div className="mt-8">
            <div className="text-center mb-6">
              <Badge className="bg-gradient-to-r from-orange-500 to-pink-500 text-white border-0 mb-3 px-4 py-1">
                <Crown className="h-4 w-4 mr-1" weight="fill" /> PREMIUM
              </Badge>
              <h2 className="font-['Chivo'] text-2xl font-black text-gray-900 mb-2">
                Unlock All Features!
              </h2>
            </div>

            {/* Plans */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Free Trial */}
              <Card className="border border-gray-200 hover:border-green-400 transition-all cursor-pointer" onClick={() => navigate('/premium')}>
                <CardContent className="pt-6 pb-6 text-center">
                  <Gift className="h-8 w-8 text-green-600 mx-auto mb-2" weight="duotone" />
                  <h3 className="font-bold text-lg mb-1">Free Trial</h3>
                  <p className="text-3xl font-black text-green-600">FREE</p>
                  <p className="text-sm text-gray-500">1 महिना</p>
                  <div className="mt-3 text-xs text-green-600 font-medium">
                    <Clock className="inline h-3 w-3 mr-1" />
                    No Card Required
                  </div>
                </CardContent>
              </Card>

              {/* Monthly */}
              <Card className="border border-gray-200 hover:border-orange-400 transition-all cursor-pointer" onClick={() => navigate('/premium')}>
                <CardContent className="pt-6 pb-6 text-center">
                  <h3 className="font-bold text-lg mb-1">Monthly</h3>
                  <p className="text-3xl font-black text-gray-900">₹99</p>
                  <p className="text-sm text-gray-500">/month</p>
                  <div className="mt-3 text-xs text-gray-500">
                    Cancel anytime
                  </div>
                </CardContent>
              </Card>

              {/* Yearly */}
              <Card className="border-2 border-orange-500 shadow-lg relative overflow-hidden cursor-pointer" onClick={() => navigate('/premium')}>
                <div className="absolute top-0 right-0 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                  BEST VALUE
                </div>
                <CardContent className="pt-6 pb-6 text-center">
                  <h3 className="font-bold text-lg mb-1">Yearly</h3>
                  <p className="text-sm text-gray-400 line-through">₹1188</p>
                  <p className="text-3xl font-black text-orange-600">₹999</p>
                  <p className="text-sm text-gray-500">/year</p>
                  <Badge className="mt-2 bg-green-100 text-green-700 border-0">
                    2 महिने FREE! 🎁
                  </Badge>
                </CardContent>
              </Card>
            </div>

            {/* Benefits Grid */}
            <Card className="bg-gradient-to-br from-orange-50 to-pink-50 border-orange-200 mb-6">
              <CardContent className="pt-6">
                <h3 className="font-bold text-lg mb-4 text-center">Premium मध्ये मिळेल:</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {benefits.map((benefit, i) => {
                    const Icon = benefit.icon;
                    return (
                      <div key={i} className="flex items-start gap-2">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Check className="h-4 w-4 text-green-600" weight="bold" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{benefit.title}</p>
                          <p className="text-xs text-gray-500">{benefit.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* CTA */}
            <div className="text-center">
              <Button
                onClick={() => navigate('/premium')}
                className="bg-gradient-to-r from-[#FF5A36] to-orange-500 hover:from-[#E04828] hover:to-orange-600 text-white rounded-full px-8 py-6 text-lg font-bold shadow-xl"
              >
                <Lightning className="h-5 w-5 mr-2" weight="fill" />
                Premium Plan घ्या →
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
